import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { VbLxpVideoPlayerComponent, StreamService } from '@vbrick/vb-lxp-lib';
import { StreamData } from 'projects/vb-lxp-lib/src/lib/shared/stream.token';

export type SlotItem =
  | { type: 'timing'; id: string; streamData: StreamData }
  | { type: 'mainStream' }
  | { type: 'twitchStream' };

@Injectable({
  providedIn: 'root',
})
export class LivestreamViewService {
  public occupiedSlots = signal<SlotItem[]>([{ type: 'mainStream' }]);
  public streamData = computed(() => {
    const slots = this.occupiedSlots();
    return slots.map((s) => {
      switch (s.type) {
        case 'timing':
          return this.streamService.getRaceStreamData().find((rsd) => rsd.carNumber === s.id);
        case 'mainStream':
          return this.streamService.getMainStreamData();
        case 'twitchStream':
          return this.streamService.getTwitchStreamData();
      }
    });
  });

  public currentPlayers: VbLxpVideoPlayerComponent[] = [];

  public streamService = inject(StreamService);

  constructor() {
    effect(() => {
      this.occupiedSlots();
      for (let i = 0; i < this.currentPlayers.length; i++) {
        const currentPlayer = this.currentPlayers[i];
        const currentStreamData = currentPlayer.streamData;
        if (!this.streamData().includes(currentStreamData)) {
          const newStreamData = this.streamData().filter(
            (sd) => !this.currentPlayers.map((p) => p.streamData).includes(sd),
          );
          if (newStreamData[0]?.id === 'twitch-stream') {
            currentPlayer.setStreamData(newStreamData[0]);
            currentPlayer.setTwitchStream();
          } else {
            currentPlayer.setStreamData(newStreamData[0]);
          }
        }
      }
    });
  }

  public addSlotItem(slotItem: SlotItem) {
    this.occupiedSlots.update((os) => [...os, slotItem]);
  }

  public removeSlotItem(slotItem: SlotItem) {
    if (slotItem.type === 'timing') {
      this.occupiedSlots.update((os) =>
        os.filter((s) => {
          if (s.type === 'timing') {
            return s.id !== slotItem.id;
          }
          return true;
        }),
      );
    } else {
      this.occupiedSlots.update((os) => os.filter((s) => s.type !== slotItem.type));
    }
  }

  public pruneOldest(overflow: number): SlotItem[] {
    const current = this.occupiedSlots();
    if (overflow <= 0 || current.length === 0) {
      return [];
    }

    const removeCount = Math.min(overflow, current.length);

    const removed = current.slice(0, removeCount);
    const remaining = current.slice(removeCount);

    this.occupiedSlots.set(remaining);
    for (let slot of removed) {
      const player = this.currentPlayers.find((p) => {
        if (slot.type === 'timing') {
          return p.streamData === slot.streamData;
        } else {
          return p.streamData === this.streamService.getMainStreamData();
        }
      });
      if (player) {
        player.streamData = null;
      }
    }
    return removed;
  }

  public updateCurrentPlayers(players: VbLxpVideoPlayerComponent[]) {
    this.currentPlayers = players;
    for (let i = 0; i < this.occupiedSlots().length; i++) {
      const currentSlot = this.occupiedSlots()[i];
      let streamData: StreamData;
      if (currentSlot.type === 'timing') {
        streamData = this.streamService
          .getRaceStreamData()
          .find((rsd) => rsd.carNumber === currentSlot.id);
      } else if (currentSlot.type === 'mainStream') {
        streamData = this.streamService.getMainStreamData();
      }
      if (currentSlot.type === 'twitchStream') {
        this.currentPlayers[i].setTwitchStream();
      } else {
        this.currentPlayers[i].setStreamData(streamData);
      }
    }
  }
}
